import { Component, Input, Output, OnInit, ViewEncapsulation, ViewChild, EventEmitter, TemplateRef, SimpleChanges, ChangeDetectorRef, ChangeDetectionStrategy, HostListener } from '@angular/core';
import { OnChanges, OnDestroy } from '@angular/core/src/metadata/lifecycle_hooks';
import { FormControl } from '@angular/forms';

import { Observable } from 'rxjs/Observable';
import { Subscription, Subscriber, Subject } from 'rxjs';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/distinctUntilChanged';

import { DatatableComponent, ColumnMode, DataTableSelectionComponent, DataTablePagerComponent } from '@swimlane/ngx-datatable';

import { AppDatatableConfigOptions } from '../models';
import { AppBasicTableHelper } from './basic-table-helper.utils';
import { AppDatatableCellTypes, AppDatatableTreeStatus, AppDatatableActionType } from '../enums';
import { PaginationInfo } from '../../../client-core/api-response-models';

@Component({
  selector: 'app-basic-table',
  templateUrl: './basic-table.component.html',
  styleUrls: ['./basic-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
  //encapsulation: ViewEncapsulation.None //works only with css??
})

export class AppBasicTableComponent implements OnInit, OnChanges, OnDestroy {

  _rows: Array<any>;
  @Input()
  public rows: Observable<any>;

  @Input()
  public appendRows: Observable<any>;

  _paginationInfo: PaginationInfo;
  @Input()
  public paginationInfo: Observable<PaginationInfo>;

  _columns: Array<any>;
  @Input()
  public columns: Observable<Array<any>>;

  _displayColumns: Array<any>;
  @Input()
  public displayColumns: Observable<Array<any>>;

  _config: AppDatatableConfigOptions;
  @Input()
  public config: Observable<AppDatatableConfigOptions>;

  @Input()
  public pageSize: number;

  @Input()
  public pageSizeOptions: Array<number>;

  @Input()
  public searchControl: FormControl;

  @Input()
  public searchModel: Subject<any>;

  @Output()
  public tableAction: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  public rowActivate: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  public rowSelect: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  public tableActionDone: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('basicTable')
  private ngxTable: DatatableComponent;

  @ViewChild('textTemplate')
  private textTemplate: TemplateRef<any>;

  @ViewChild('usdTemplate')
  private usdTemplate: TemplateRef<any>;

  @ViewChild('hhmmTemplate')
  private hhmmTemplate: TemplateRef<any>;

  @ViewChild('mmddyyTemplate')
  private mmddyyTemplate: TemplateRef<any>;

  @ViewChild('rowExpanderTemplate')
  private rowExpanderTemplate: TemplateRef<any>;

  @ViewChild('rowGroupHeaderTemplate')
  private rowGroupHeaderTemplate: TemplateRef<any>;

  @ViewChild('treeExpanderTemplate')
  public treeExpanderTemplate: TemplateRef<any>;

  @ViewChild('treeCollapserTemplate')
  public treeCollapserTemplate: TemplateRef<any>;

  @ViewChild('treeDisableTemplate')
  public treeDisableTemplate: TemplateRef<any>;

  @ViewChild('treeLoaderTemplate')
  public treeLoaderTemplate: TemplateRef<any>;

  @ViewChild('datatablePager') datatablePager: DataTablePagerComponent

  private initialized: boolean = false;
  private _pageSize: number;
  private _treeFromRelation: string;
  private _externalSorting: boolean;
  private _treeLayoutEnabled: boolean;
  protected selected: Array<any> = [];
  protected navigationOptions: any = {
    pageSize: undefined,
    pageIndex: 0,
    totalCount: 0,
    hasTreeColumn: false,
    selected: [],
    searchTerm: '',
    sortBy: '',
    sortDirection: '',
    searchModel: {}
  };

  private _rowsSubscription: Subscription;
  private _appendRowsSubscription: Subscription;
  private _paginationSubscription: Subscription;
  private _columnsSubscription: Subscription;
  private _configSubscription: Subscription;

  constructor(private changeDetector: ChangeDetectorRef) {
    this._rows = [];
    this._columns = [];
    this._displayColumns = [];
    this._paginationInfo = new PaginationInfo();
    this._config = new AppDatatableConfigOptions();
    this.pageSizeOptions = [10, 50, 100];
  }

  /*
  protected canRender() : boolean {
    return (this._rows.length>0 && this._config!=undefined);
  }
  */

  ngOnInit() {
    if (!this._config) {
      throw new Error(`Table config required on init`);
    }

    if (this.searchModel) {
      this.searchModel
        .debounceTime(400)
        .subscribe(model => {
          this.navigationOptions.searchModel = model;
          this.navigationOptions.pageIndex = 0; //reset page index
          this.tableAction.emit(this._getEventNavigationOptions());
        });
    }
  }

  setDataTablePage(page: number) {
    this.datatablePager.selectPage(page);
  }

  ngOnChanges(inputChanges: SimpleChanges): void {
    // searchControl changes
    if (inputChanges.searchControl && inputChanges.searchControl.currentValue) {
      if (inputChanges.searchControl.previousValue) {
        inputChanges.searchControl.previousValue.valueChanges.unsubscribe();
      }
      console.log(">>ngOnChanges", inputChanges.searchControl.currentValue);
      this.searchControl.valueChanges
        .debounceTime(400)
        .distinctUntilChanged()
        .subscribe((filter) => {
          this.navigationOptions.searchTerm = filter;
          this.navigationOptions.pageIndex = 0; //reset page index
          this.tableAction.emit(this._getEventNavigationOptions());
        });
    }

    //config changed
    if (inputChanges.config && inputChanges.config.currentValue) {
      if (this._configSubscription) {
        this._configSubscription.unsubscribe();
      }

      this._configSubscription = this.config.subscribe((config) => {
        this._config = config;
        //set row grouping configs as needed
        if (config.rowGrouperHeight > 0 && config.groupRowsBy && config.rowGrouperLabelGetter) {
          this.ngxTable.groupHeader.template = this.rowGroupHeaderTemplate;
          this._config.groupExpansionDefault = true;
        }

        // set row expander options as needed
        if (config.rowDetailHeight > 0 && config.rowDetailTemplate) {
          this.ngxTable.rowDetail.template = config.rowDetailTemplate;
        }

        if (config.treeLayout && config.fromRelation) {
          // to be used when toggling between tree and simple table for search
          this._treeFromRelation = config.fromRelation;
          this._treeLayoutEnabled = true;
        }

        // cache few other parameters
        this._externalSorting = config.externalSorting;
        console.log("_config", this._config);
        this.changeDetector.markForCheck();
      });
    }

    // rows changed
    if (inputChanges.rows && inputChanges.rows.currentValue) {
      if (this._rowsSubscription) {
        this._rowsSubscription.unsubscribe();
      }

      this._rowsSubscription = this.rows.subscribe((items) => {
        if (!this._config.treeLayout) {
          this._rows = [...items];
        } else {
          let rows = AppBasicTableHelper.toTreeRows(items, this._config.expandAllOnInit);
          this._rows = [...rows];
        }
        this.changeDetector.markForCheck();
        this.tableActionDone.emit("rows_loaded");
      });
    }

    // appendRows changed
    if (this._config.treeLayout && inputChanges.appendRows && inputChanges.appendRows.currentValue) {
      if (this._appendRowsSubscription) {
        this._appendRowsSubscription.unsubscribe();
      }

      this._appendRowsSubscription = this.appendRows.subscribe((items) => {
        if (items && items.length) {
          //find parent index
          let parentId = items[0][this._config.fromRelation];
          if (!parentId) {
            console.log("Parent Id missing", items[0]);
            return;
          }
          let parentIndex = this._rows.findIndex(
            r => parentId === r[this._config.toRelation]
          );
          if (!parentIndex) {
            console.log("Parent Index missing", parentId);
            //return;
          }
          let temp = [];
          let spliced = 0;
          temp = this._rows;
          for (let idx = temp.length - 1; idx >= 0; idx -= 1) {
            if (temp[idx][this._config.fromRelation] == parentId) {
              temp.splice(idx, 1);
              spliced++;
            }
          }
          let rows = AppBasicTableHelper.toTreeRows(items, this._config.expandAllOnInit);
          //set parent row to expanded
          if (this._rows[parentIndex]) {
            this._rows[parentIndex].treeStatus = AppDatatableTreeStatus.EXPANDED;
          }
          //new total = (old total - spliced data + new data)
          let totalAfterAppend = this._paginationInfo.totalCount - spliced + rows.length;
          this._rows = [...temp, ...rows];
          this._paginationInfo.totalCount = totalAfterAppend;
          //this._paginationInfo.pageSize = this._rows.length;
          this.navigationOptions.pageSize = undefined; // or else it will limit number of rows
          console.log(">>toTreeAppendRows totalCount", totalAfterAppend);
          this.changeDetector.markForCheck();
          this.tableActionDone.emit("rows_appended");
        }
      });
    }

    //pageSize changed
    if (inputChanges.pageSize && inputChanges.pageSize.currentValue
      && (inputChanges.pageSize.currentValue != inputChanges.pageSize.previousValue)) {
      this.onPageSizeChange(inputChanges.pageSize.currentValue);
    }

    //columns changed
    if (inputChanges.columns && inputChanges.columns.currentValue) {
      if (this._columnsSubscription) {
        this._columnsSubscription.unsubscribe();
      }

      let dispCols = [];
      this._columnsSubscription = this.columns.subscribe((columns) => {
        columns.forEach((col) => {
          if (this._config.treeLayout && col.isTreeColumn) {
            this.navigationOptions.hasTreeColumn = true;
            col.treeCellClass = "col-md-12 no-padding"; // CUSTOM code added to ngx
            col.treeClickableClass = ""; // CUSTOM code added to ngx
            col.treeExpanderTemplate = this.treeExpanderTemplate;
            col.treeCollapserTemplate = this.treeCollapserTemplate;
            col.treeDisableTemplate = this.treeDisableTemplate;
            col.treeLoaderTemplate = this.treeLoaderTemplate;
            col.lockDisplay = true;
            console.log(">> isTreeColumn", col);
          }

          if (!col.cellTemplate) {
            switch (col.cellType) {
              case AppDatatableCellTypes.READONLY_MONEY: {
                col.cellTemplate = this.usdTemplate;
                break;
              }
              case AppDatatableCellTypes.READONLY_HHMM: {
                col.cellTemplate = this.hhmmTemplate;
                break;
              }
              case AppDatatableCellTypes.READONLY_MMDDYY: {
                col.cellTemplate = this.mmddyyTemplate;
                break;
              }
              default:
                col.cellTemplate = this.textTemplate;
            }
          }

          if (!col.hideOnInit) {
            dispCols.push(col);
          }
        });

        if (this._config.rowExpandable && columns.length > 0) {
          let rowExpander = this._getRowExpanderColumn();
          console.log("expander: ", rowExpander);
          dispCols = [...rowExpander, ...dispCols];
          this._columns = [...rowExpander, ...columns];
        } else {
          this._columns = [...columns];
        }
        this._displayColumns = [...dispCols];
        console.log(">> _columns", this._columns);
        this.changeDetector.markForCheck();
      });
    }

    //paginationInfo changed
    if (inputChanges.paginationInfo && inputChanges.paginationInfo.currentValue) {
      if (this._paginationSubscription) {
        this._paginationSubscription.unsubscribe();
      }

      this._paginationSubscription = this.paginationInfo.subscribe((pInfo) => {
        this._paginationInfo = pInfo;
        if (!this.navigationOptions.pageSize) {
          this.navigationOptions.pageSize = this._paginationInfo.pageSize;
          this._pageSize = this._paginationInfo.pageSize;
        }
        this.changeDetector.markForCheck();
      });
    }

    //displayColumns changed
    if (inputChanges.displayColumns && inputChanges.displayColumns.currentValue) {
      this.displayColumns.subscribe((dispCols) => {
        let dispColSet = dispCols.reduce((hash, s) =>
          hash.add(s.prop), new Set()
        );
        let newDispCols = this._columns.filter(function (c) {
          // always send columns that can't be hidden
          return (c.lockDisplay || this.has(c.prop));
        }, dispColSet);

        this._displayColumns = [...newDispCols];
        this.changeDetector.markForCheck();
        console.log(">>displayColumns: ", newDispCols);
      });
    }
  }

  ngAfterViewInit() {
    //TODO: hack to fix some flex issues
    // still not resizing headers
    //this.ngxTable.columnMode = ColumnMode.force;
    //window.dispatchEvent(new Event('resize'));
  }

  ngOnDestroy(): void {
    if (this._columnsSubscription) {
      this._columnsSubscription.unsubscribe();
    }
    if (this._paginationSubscription) {
      this._paginationSubscription.unsubscribe();
    }
    if (this._rowsSubscription) {
      this._rowsSubscription.unsubscribe();
    }
    if (this._appendRowsSubscription) {
      this._rowsSubscription.unsubscribe();
    }

    if (this.searchModel)
      this.searchModel.unsubscribe();
  }

  private _getRowExpanderColumn(): any {
    let rowExpanderCol = [{
      width: 40,
      cellTemplate: this.rowExpanderTemplate,
      cellClass: "text-center",
      resizeable: false,
      sortable: false,
      draggable: false,
      canAutoResize: false,
      lockDisplay: true
    }];
    return rowExpanderCol;
  }

  protected onPageSizeChange(pageSize: number): void {
    this.navigationOptions.pageSize = pageSize;
    this._pageSize = pageSize;
    //reset to first page
    this.navigationOptions.pageIndex = 0;
    this.tableAction.emit(this._getEventNavigationOptions());
  }

  protected onPageChange(event: any): void {
    this.navigationOptions.pageIndex = parseInt(event.page) - 1;
    this.tableAction.emit(this._getEventNavigationOptions());
  }

  protected onPage(event: any): void {
    console.log(">> onPage", event);
    //suppress
  }

  protected onRowExpanderToggle(row: any, event: any): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    console.log("row detail template", this.ngxTable.rowDetail.template)
    if (this.ngxTable.rowDetail.template) {
      this.ngxTable.rowDetail.toggleExpandRow(row);
    }
  }

  protected onRowDetailToggle(event: any) {
    console.log(">>onRowDetailToggle", event);
  }


  protected onGroupExpander(event: any, group: any) {
    console.log(">> onGroupExpander", event, group);
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.ngxTable.groupHeader.toggleExpandGroup(group);
  }

  protected onGroupHeaderToggle(event: any) {
    console.log(">>onGroupHeaderToggle", event);
  }

  protected onTreeAction(event: any): void {
    console.log(">>> onTreeAction", event);

    if (this._config.treeLayout && !this.navigationOptions.hasTreeColumn) {
      throw new Error(`Tree expansion column is required  for tree layout, set isTreeColumn property for a column`);
    }

    let rowIndex = event.rowIndex;
    let row = event.row;
    row.rowIndex = rowIndex; //cache value in row (might be useful)
    this.navigationOptions.selected = [row];
    this.selected = [row];
    // updating row from event doesn't seem to need explicit table refresh
    if (row.treeStatus == AppDatatableTreeStatus.DISABLED || row.treeStatus == AppDatatableTreeStatus.LOADING) {
      return;
    } else if (row.treeStatus == AppDatatableTreeStatus.EXPANDED) {
      //collapse and force tree update
      this._rows[rowIndex].treeStatus = AppDatatableTreeStatus.COLLAPSED;
      this._rows = [...this._rows];
    } else if (this._config.exapandLocally && row.treeStatus == AppDatatableTreeStatus.COLLAPSED) {
      this._rows[rowIndex].treeStatus = AppDatatableTreeStatus.EXPANDED;
      this._rows = [...this._rows];
    } else if (row.treeStatus == AppDatatableTreeStatus.COLLAPSED) {
      //set loading state 
      this._rows[rowIndex].treeStatus = AppDatatableTreeStatus.LOADING;
      //inject current row into navigation state and load data again
      this.tableAction.emit(this._getEventNavigationOptions(true, AppDatatableActionType.EXPAND));
    }
  }

  protected onSort(event: any): void {
    if (event.sorts && event.sorts.length > 0) {
      let sortBy = "";
      let sortDirection = "";
      event.sorts.forEach((sort) => {
        sortBy = ((sortBy == "") ? "" : ",") + sort.prop;
        sortDirection = ((sortDirection == "") ? "" : ",") + sort.dir;
      });
      this.navigationOptions.sortBy = sortBy;
      this.navigationOptions.sortDirection = sortDirection;
      this.tableAction.emit(this._getEventNavigationOptions(false, AppDatatableActionType.SORT));
    }
  }

  protected onSelect(event: any): void {
    console.log(">> onSelect", event);
    this.navigationOptions.selected = event.selected;
    this.rowSelect.emit(this._getEventNavigationOptions(true));
  }

  protected onClick(event: any): void {
    //nohup
  }

  protected onActivate(event: any): void {
    //console.log(">> onActivate", event);
    //allow keyboard row selection without enter
    if (event.type == "focus") {
      console.log("onActivate focus", event);
      //TODO: select row/cell here
    } else if (event.type == "click" && event.event) {
      console.log("onActivate stopPropagation");
      event.event.preventDefault();
      event.event.stopPropagation();
    }
  }

  protected onCellFocus(event: any, row: any) {
    console.log(">> onCellFocus", event, row);
  }

  public doRefresh(options = { resetPageIndex: false, forceResize: false }): void {
    if (options.resetPageIndex) {
      this.ngxTable.offset = 0;
    }
    if (options.forceResize) {
      window.dispatchEvent(new Event('resize'));
    }
  }

  private _setTreeLayout(): void {
    if (!this._config.treeLayout) {
      this._config.treeLayout = true;
      this._config.fromRelation = this._treeFromRelation;
      this._config.toRelation = this._config.idProp;
      this._config.externalSorting = false; //TODO: impl better external sorting for tree
      this._columns[this._config.treeColumnIndex].isTreeColumn = true;
      this._displayColumns[this._config.treeColumnIndex].isTreeColumn = true;
    }
  }

  private _resetTreeLayout(): void {
    if (this._config.treeLayout) {
      this._config.treeLayout = false;
      this._config.fromRelation = undefined;
      this._config.toRelation = undefined;
      this._config.externalSorting = this._externalSorting;
      this._columns[this._config.treeColumnIndex].isTreeColumn = false;
      this._displayColumns[this._config.treeColumnIndex].isTreeColumn = false;
    }
  }

  private _getEventNavigationOptions(sendSelected: boolean = false, action: AppDatatableActionType = undefined): any {

    let opts = {};
    if (this._pageSize)
      opts["pageSize"] = this._pageSize;

    if (this.navigationOptions.pageIndex)
      opts["pageIndex"] = this.navigationOptions.pageIndex;

    //toggle to simple table even if tree layout is enabled when in search mode
    if (this.navigationOptions.searchTerm && this.navigationOptions.searchTerm.trim().length > 0) {
      opts["searchTerm"] = this.navigationOptions.searchTerm.trim();
      if (this._treeLayoutEnabled) {
        this._resetTreeLayout();
      }
    } else if (this._treeLayoutEnabled) {
      this._setTreeLayout();
    }

    if (this.navigationOptions.sortBy.length > 0) {
      opts["sortBy"] = this.navigationOptions.sortBy;
      opts["sortDirection"] = this.navigationOptions.sortDirection;
    }

    if (sendSelected && this.navigationOptions.selected.length > 0) {
      opts["selected"] = this.navigationOptions.selected;
    }

    if (action) {
      opts["action"] = action;
    }

    if (this.navigationOptions.searchModel)
      opts = Object.assign(opts, this.navigationOptions.searchModel);

    return opts;
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/datatable/basic/basic-table.component.ts